import { useEffect, useState } from "react";
import { X, Search, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminModalOverlay, AdminModalCard, AdminModalBody } from "@/components/AdminModal";

const TERRITORY_STATUS_LABELS = { disponible: "Disponible", reserve: "Réservé", attribue: "Actif" };

export const AdminProposeTerritoryModal = ({ partner, territories, currentProposed, onClose, onProposed }) => {
  const isReplacement = !!currentProposed;
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); return; }
    let active = true;
    const local = territories.filter((t) => `${t.code} ${t.name} ${t.department}`.toLowerCase().includes(q.toLowerCase()));
    const params = /^\d{2,5}$/.test(q) ? { cp: q.padEnd(5, "") } : { q };
    const timer = setTimeout(() => {
      api.get("/communes/search", { params }).then((res) => {
        if (!active) return;
        const codes = new Set((res.data || []).map((c) => c.insee_code));
        const communeMatches = territories.filter((t) => (t.commune_insee_codes || []).some((c) => codes.has(c)));
        const merged = [...local, ...communeMatches].filter((t, i, arr) => arr.findIndex((x) => x.id === t.id) === i);
        setResults(merged.slice(0, 15));
      }).catch(() => { if (active) setResults(local.slice(0, 15)); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [term, territories]);

  const select = (t) => {
    if (t.status !== "disponible") { toast.error("Ce territoire n'est pas disponible — il ne peut pas être proposé."); return; }
    setSelected(t); setTerm(""); setResults([]);
  };

  const submit = async () => {
    if (!selected) { toast.error("Sélectionnez un territoire disponible."); return; }
    if (isReplacement && !reason.trim()) { toast.error("Indiquez un motif de remplacement."); return; }
    setSaving(true);
    try {
      await api.post(`/admin/partners/${partner.id}/propose-territory`, {
        territory_id: selected.id, reason: reason.trim() || undefined,
      });
      toast.success(`Territoire « ${selected.name} » proposé au partenaire.`);
      onProposed();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModalOverlay onClose={onClose} testId="propose-territory-modal">
      <AdminModalCard maxWidth="max-w-md">
        <div className="flex items-center justify-between border-b border-slate-100 px-7 py-5">
          <h3 className="font-display text-lg font-bold text-navy">{isReplacement ? "Changer le territoire proposé" : "Proposer un territoire"}</h3>
          <button data-testid="propose-territory-close" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <AdminModalBody>
          {isReplacement && (
            <p className="mb-3 text-xs text-slate-500">
              Proposition actuelle : <b>{currentProposed.name}</b> ({currentProposed.code})
            </p>
          )}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="propose-territory-search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Commune, code postal, nom ou code CH-xx-xx…"
              className="w-full rounded-full border border-slate-200 py-2 pl-9 pr-4 text-sm"
            />
          </div>
          {results.length > 0 && (
            <ul className="mt-2 max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200">
              {results.map((t) => (
                <li key={t.id}>
                  <button
                    type="button" data-testid={`propose-territory-option-${t.code}`}
                    disabled={t.status !== "disponible"}
                    onClick={() => select(t)}
                    className="flex w-full items-center justify-between gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <span><b className="text-navy">{t.name}</b> <span className="text-slate-400">— {t.code}</span></span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${t.status === "disponible" ? "bg-sage/20 text-navy" : "bg-red-100 text-red-600"}`}>
                      {TERRITORY_STATUS_LABELS[t.status] || t.status}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {selected && (
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-cognac/30 bg-cognac/5 p-3">
              <MapPin className="h-4 w-4 shrink-0 text-cognac" />
              <p className="text-sm font-bold text-navy">{selected.name} — {selected.code}</p>
            </div>
          )}
          {isReplacement && (
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Motif du remplacement *</label>
              <textarea
                data-testid="propose-territory-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Ex. le territoire initialement proposé a été attribué à un autre partenaire"
                className="mt-1 w-full rounded-xl border border-slate-200 p-2.5 text-sm"
              />
            </div>
          )}
          <button
            type="button" data-testid="propose-territory-submit" onClick={submit} disabled={saving}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isReplacement ? "Changer la proposition" : "Proposer ce territoire"}
          </button>
        </AdminModalBody>
      </AdminModalCard>
    </AdminModalOverlay>
  );
};
